import React, { useEffect } from "react";
import "./privacy.css";

const PrivacyPolicy: React.FC = () => {
  useEffect(() => {
    document.title = "Privacy Policy — REQUESTA";
  }, []);

  return (
    <main className="rqpv-wrap" role="main">
      <div className="rqpv-card">
        <header className="rqpv-header">
          <div className="rqpv-title">Privacy Policy</div>
          <div className="rqpv-meta">
            <span>Effective Date: September 30, 2025</span>
            <span>Company: REQUESTA LLC (Wyoming, USA)</span>
            <span>Contact: <a className="rqpv-link" href="mailto:support@requesta.app">support@requesta.app</a></span>
          </div>
        </header>


        <section id="rqpv-controller" className="rqpv-section">
          <div className="rqpv-h2">1. Data Controller</div>
          <div className="rqpv-text">
            REQUESTA LLC is the data controller responsible for your personal data.
            We can be contacted at <a className="rqpv-link" href="mailto:support@requesta.app">support@requesta.app</a>.
          </div>
          <div className="rqpv-text" style={{ marginTop: "10px" }}>
            We use trusted service providers as data processors, including:
          </div>
          <ul className="rqpv-list">
            <li className="rqpv-li">Stripe, Inc. (payment processing)</li>
            <li className="rqpv-li">Twilio (SMS notifications)</li>
            <li className="rqpv-li">AWS, Vercel Pro, MongoDB (hosting and infrastructure)</li>
          </ul>
        </section>

        <section id="rqpv-info" className="rqpv-section">
          <div className="rqpv-h2">2. Information We Collect</div>
          <div className="rqpv-text">We collect only what is necessary to provide the App:</div>
          <ul className="rqpv-list">
            <li className="rqpv-li"><strong>Contact Information:</strong> Name, email address, phone number (when voluntarily provided).</li>
            <li className="rqpv-li"><strong>Identifiers:</strong> Account login credentials (hashed), device identifiers.</li>
            <li className="rqpv-li"><strong>Financial Information:</strong> Payment details processed via Stripe, Apple Pay, and Link (we do not store card data).</li>
            <li className="rqpv-li"><strong>Usage Data:</strong> Song requests, in-app messages, payment history.</li>
            <li className="rqpv-li"><strong>Diagnostics:</strong> Limited technical logs and crash data for stability and performance.</li>
          </ul>
          <div className="rqpv-text" style={{ marginTop: "10px" }}>
            We do not collect location data, health data, biometric data, or data for advertising tracking.
          </div>
        </section>

        <section id="rqpv-purposes" className="rqpv-section">
          <div className="rqpv-h2">3. Purposes of Processing</div>
          <div className="rqpv-text">We process personal data for the following purposes:</div>
          <ul className="rqpv-list">
            <li className="rqpv-li">App Functionality (enabling requests, account features).</li>
            <li className="rqpv-li">Account Management (registration, login, profile).</li>
            <li className="rqpv-li">SMS Notifications (delivering request alerts and updates via Twilio, with your opt-in consent).</li>
            <li className="rqpv-li">Payments and Payouts (via Stripe, Apple Pay, and Link).</li>
            <li className="rqpv-li">App Improvement (stability, performance, and bug fixes).</li>
          </ul>
        </section>

        <section id="rqpv-share" className="rqpv-section">
          <div className="rqpv-h2">4. Data Sharing</div>
          <div className="rqpv-text">We share data only with trusted processors necessary to operate the App:</div>
          <ul className="rqpv-list">
            <li className="rqpv-li">Stripe for payments.</li>
            <li className="rqpv-li">Twilio for SMS notifications.</li>
            <li className="rqpv-li">AWS, Vercel Pro, MongoDB for hosting and technical services.</li>
          </ul>
          <div className="rqpv-text" style={{ marginTop: "10px" }}>
            We do not sell, rent, or share personal data with advertisers. We do not use data for cross-app tracking.
            Your phone number and SMS consent are never shared with third parties for marketing purposes.
          </div>
        </section>

        <section id="rqpv-sms" className="rqpv-section">
          <div className="rqpv-h2">5. Phone Numbers &amp; SMS</div>
          <div className="rqpv-text">
            We collect your mobile phone number when you voluntarily provide it during account creation or in your account settings.
            Your phone number is used solely to send you transactional SMS notifications related to your account activity
            on Request App — including incoming song requests, payment alerts, and service updates.
          </div>
          <div className="rqpv-text" style={{ marginTop: "10px" }}>
            We do not share your phone number or SMS consent with any third parties for marketing or promotional purposes.
            SMS messages are delivered through our communications provider (Twilio) strictly for service-related notifications.
          </div>
          <div className="rqpv-text" style={{ marginTop: "10px" }}>
            You can opt out of SMS messages at any time by replying <strong>STOP</strong> to any message.
            Reply <strong>HELP</strong> for assistance. Standard message and data rates may apply.
            Message frequency varies based on your account activity.
          </div>
        </section>

        <section id="rqpv-consent" className="rqpv-section">
          <div className="rqpv-h2">6. Consent &amp; User Controls</div>
          <ul className="rqpv-list">
            <li className="rqpv-li">SMS notifications are provided only if you opt in by providing your phone number and enabling notifications in your account settings. You may opt out at any time by replying STOP or disabling SMS in your settings.</li>
            <li className="rqpv-li">You may delete your account at any time from within the App or by contacting <a className="rqpv-link" href="mailto:support@requesta.app">support@requesta.app</a>.</li>
          </ul>
        </section>

        <section id="rqpv-retention" className="rqpv-section">
          <div className="rqpv-h2">7. Data Retention</div>
          <ul className="rqpv-list">
            <li className="rqpv-li"><strong>Account data:</strong> Stored as long as your account is active. Deleted within 30–60 days after account deletion.</li>
            <li className="rqpv-li"><strong>Payment data:</strong> Retained by Stripe in accordance with financial regulations.</li>
            <li className="rqpv-li"><strong>Phone number:</strong> Retained only while SMS notifications are enabled.</li>
            <li className="rqpv-li"><strong>Diagnostics/crash logs:</strong> Retained for a limited period necessary for stability improvements.</li>
          </ul>
        </section>

        <section id="rqpv-security" className="rqpv-section">
          <div className="rqpv-h2">8. Security &amp; International Transfers</div>
          <div className="rqpv-text">
            Data is encrypted in transit (TLS/HTTPS) and stored securely on cloud servers.
            Access is restricted to authorized personnel only.
            Data may be processed internationally (e.g., U.S.-based servers).
          </div>
        </section>

        <section id="rqpv-rights" className="rqpv-section">
          <div className="rqpv-h2">9. Your Rights</div>
          <div className="rqpv-text">You have the right to:</div>
          <ul className="rqpv-list">
            <li className="rqpv-li">Access your data.</li>
            <li className="rqpv-li">Correct inaccurate data.</li>
            <li className="rqpv-li">Request deletion of your account and personal data.</li>
            <li className="rqpv-li">Request a copy of your data in portable format.</li>
            <li className="rqpv-li">Contact us with complaints at <a className="rqpv-link" href="mailto:support@requesta.app">support@requesta.app</a>.</li>
          </ul>
        </section>

        <section id="rqpv-children" className="rqpv-section">
          <div className="rqpv-h2">10. Children's Privacy</div>
          <div className="rqpv-text">
            The App is not directed to children under 13 (or under 16 in jurisdictions where a higher minimum age applies).
            We do not knowingly collect personal data from children.
          </div>
        </section>

        <section id="rqpv-changes" className="rqpv-section">
          <div className="rqpv-h2">11. Updates to This Policy</div>
          <div className="rqpv-text">
            We may update this Privacy Policy. Changes will be posted on our website with a revised "Effective Date."
          </div>
        </section>

        <div className="rqpv-updated">Effective Date: September 30, 2025</div>
      </div>
    </main>
  );
};

export default PrivacyPolicy;
